import {
  CREDIT_TYPE,
  WALLET_TRANSACTION_TYPE,
} from '@monorepo/shared/schemas/common'
import type { ColumnDef } from '@tanstack/react-table'
import { CalendarIcon, Eye, MoreHorizontal, WalletIcon } from 'lucide-react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { walletService } from '../../services/walletService'
import type { Wallet, WalletTransaction } from '../../types/wallet.types'

interface WalletTransactionsProps {
  wallet: Wallet
  transactionsData?: {
    data: WalletTransaction[]
    meta: {
      page: number
      limit: number
      total: number
      pages: number
      has_next: boolean
      has_prev: boolean
    }
  }
  isLoading?: boolean
  onViewTransactionDetails?: (transaction: WalletTransaction) => void
}

export function WalletTransactions({
  wallet: _wallet,
  transactionsData,
  isLoading = false,
  onViewTransactionDetails,
}: WalletTransactionsProps) {
  // Formatting functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (amount: number, currency = 'USD') => {
    return walletService.formatAmount(amount, currency)
  }

  const getTransactionIcon = (type: WalletTransaction['type']) => {
    switch (type) {
      case WALLET_TRANSACTION_TYPE.credit:
        return <WalletIcon className='w-4 h-4' />
      case WALLET_TRANSACTION_TYPE.debit:
        return <WalletIcon className='w-4 h-4' />
      case WALLET_TRANSACTION_TYPE.refund:
        return <WalletIcon className='w-4 h-4' />
      case 'charge':
        return <WalletIcon className='w-4 h-4' />
      default:
        return <WalletIcon className='w-4 h-4' />
    }
  }

  const getTransactionTypeColor = (type: WalletTransaction['type']) => {
    return walletService.getTransactionTypeColor(type)
  }

  const handleViewTransactionDetails = (transaction: WalletTransaction) => {
    onViewTransactionDetails?.(transaction)
  }

  // Define table columns
  const transactionColumns: ColumnDef<WalletTransaction>[] = [
    {
      accessorKey: 'created_at',
      header: 'Date',
      size: 200,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <CalendarIcon className='w-4 h-4 text-muted-foreground' />
          <span className='text-sm'>{formatDate(row.original.created_at)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 250,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.description}</div>
          {row.original.reference_id && (
            <div className='text-sm text-muted-foreground'>
              Ref: {row.original.reference_type?.toUpperCase()} #
              {row.original.reference_id}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 180,
      cell: ({ row }) => {
        const isCredit =
          row.original.type === WALLET_TRANSACTION_TYPE.credit ||
          row.original.type === WALLET_TRANSACTION_TYPE.refund
        const color = getTransactionTypeColor(row.original.type)
        return (
          <div>
            <div
              className={`font-medium ${color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-gray-600'}`}
            >
              {isCredit ? '+' : '-'}
              {formatCurrency(row.original.amount, row.original.currency)}
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'conversion_rate_snapshot',
      header: 'Rate',
      size: 100,
      cell: ({ row }) => {
        if (!row.original.conversion_rate_snapshot) {
          return <span className='text-muted-foreground'>-</span>
        }

        return (
          <div className='text-sm text-muted-foreground'>
            {row.original.conversion_rate_snapshot.toFixed(2)}
          </div>
        )
      },
    },
    {
      accessorKey: 'credit_type',
      header: 'Credit Type',
      size: 180,
      cell: ({ row }) => {
        if (!row.original.credit_type)
          return <span className='text-muted-foreground'>-</span>

        return (
          <Badge
            variant={
              row.original.credit_type === CREDIT_TYPE.free
                ? 'secondary'
                : 'outline'
            }
            className={
              row.original.credit_type === CREDIT_TYPE.free
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                : ''
            }
          >
            {row.original.credit_type === CREDIT_TYPE.free
              ? 'Free'
              : 'Purchased'}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'expiry_date',
      header: 'Expiry Date',
      size: 200,
      cell: ({ row }) => {
        if (!row.original.expiry_date) {
          return <span className='text-muted-foreground'>No expiry</span>
        }

        const isExpired =
          row.original.expired_at ||
          new Date(row.original.expiry_date) < new Date()
        const formattedDate = new Date(
          row.original.expiry_date
        ).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })

        return (
          <span
            className={
              isExpired
                ? 'text-red-600 line-through dark:text-red-400'
                : 'text-muted-foreground'
            }
          >
            {formattedDate}
            {isExpired && ' (Expired)'}
          </span>
        )
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 200,
      cell: ({ row }) => {
        const typeText = walletService.getTransactionTypeText(row.original.type)
        const color = getTransactionTypeColor(row.original.type)
        const textColor =
          color === 'green'
            ? 'text-green-600 dark:text-green-400'
            : color === 'red'
              ? 'text-red-600 dark:text-red-400'
              : 'text-muted-foreground'

        return (
          <div className={`flex items-center gap-2 ${textColor}`}>
            {getTransactionIcon(row.original.type)}
            <span className='font-medium'>{typeText}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'balance_after',
      header: 'Balance After',
      size: 150,
      cell: ({ row }) => (
        <div className='text-sm text-muted-foreground'>
          {formatCurrency(row.original.balance_after, row.original.currency)}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 40,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
              <MoreHorizontal className='w-4 h-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-48'>
            <DropdownMenuItem
              onClick={() => handleViewTransactionDetails(row.original)}
            >
              <Eye className='w-4 h-4 mr-2' />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-12 text-muted-foreground'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4'></div>
            <h3 className='text-lg font-medium mb-2'>
              Loading transactions...
            </h3>
          </div>
        </CardContent>
      </Card>
    )
  }

  const transactions = transactionsData?.data || []

  return (
    <div className='bg-transparent border'>
      {transactions.length > 0 ? (
        <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
          <EnhancedTable
            data={transactions}
            columns={transactionColumns.map(col => ({
              ...col,
              enableSorting: false,
            }))}
            height='500px'
            className='bg-transparent'
          />
        </div>
      ) : (
        <Card>
          <CardContent>
            <div className='text-center py-12 text-muted-foreground'>
              <WalletIcon className='w-12 h-12 mx-auto mb-4 opacity-50' />
              <h3 className='text-lg font-medium mb-2'>
                No transactions found
              </h3>
              <p className='text-sm max-w-sm mx-auto mb-4'>
                Transaction history will appear here as the customer uses
                services and makes payments.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
